import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TransactionDetailsFormatterService } from '@rd/core//services/transaction-details-formatter/transaction-details-formatter.service';
import { LoyaltyCurrencyService } from '@rd/core/services/loyalty-currency/loyalty-currency.service';
import { NumberService } from '@rd/core/services/number/number.service';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { LoyaltyProgramHelper } from '@rd/helpers/loyalty-program.helper';
import { orderItemFilter } from '@rd/helpers/order.helper';
import { DynamicListItem } from '@rd/shared-legacy/components/dynamic-list/dynamic-list-item.type';
import {
  IconOptions,
  LoyaltyProgram,
  Order,
  OrderItemStatus,
  OrderItemType,
  PointsTransferOrderItem,
  PointsTransferOrderItemMetadata,
  VoucherOrderItemMetadata,
} from '@rd/types';
import { TransferConnectV2SettingService } from '../../services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';

@Component({
  selector: 'rd-tcv2-success-detail',
  templateUrl: './success-detail.component.html',
  styleUrls: ['./success-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TcV2SuccessDetailComponent implements OnChanges {
  OrderItemStatus = OrderItemStatus;

  @HostBinding('class.rd-tcv2-success-detail') componentClass = true;

  @Input() loyaltyProgram: LoyaltyProgram;
  @Input() pointsTransferOrder: Order;

  flippableIconOptions: IconOptions = {
    flippable: true,
  };

  pointsTransferDetails: DynamicListItem[] = [];

  brandIconUrl = this.urlUtilsService.imageUrl(
    '/images/branding/dark-logo.png',
  );

  hasDiscount = false;

  loyaltyProgramNameAttribute =
    this.transferConnectV2SettingService.loyaltyProgramNameAttribute;

  targetLoyaltyCurrencyNameAttribute =
    this.transferConnectV2SettingService.targetLoyaltyCurrencyNameAttribute;

  pointsTransferOrderItemData:
    | PointsTransferOrderItemMetadata
    | VoucherOrderItemMetadata;

  constructor(
    private loyaltyCurrencyService: LoyaltyCurrencyService,
    private loyaltyProgramHelper: LoyaltyProgramHelper,
    private numberService: NumberService,
    private urlUtilsService: UrlUtilsService,
    private transferConnectV2SettingService: TransferConnectV2SettingService,
    private transactionDetailFormatterSvc: TransactionDetailsFormatterService,
    private translateService: TranslateService,
  ) {}

  ngOnChanges({ pointsTransferOrder }: SimpleChanges): void {
    if (!!pointsTransferOrder) {
      this.pointsTransferOrderItemData =
        this.pointsTransferOrder.orderItems.find(
          orderItemFilter<PointsTransferOrderItem>(
            OrderItemType.PointsTransferOrderItem,
            // [Hau] need to support voucher order item as well
            // as it is part of the same points transfer flow
            OrderItemType.VoucherOrderItem,
          ),
        )!.attributes.data;

      this.hasDiscount =
        !!this.pointsTransferOrderItemData.promoCode &&
        this.pointsTransferOrder.pointsPaid <
          this.pointsTransferOrder.pointsAmount;

      const {
        firstName,
        lastName,
        number: accountNumber,
        dateOfBirth,
      } = this.pointsTransferOrderItemData.membershipInformation;
      this.pointsTransferDetails =
        this.transactionDetailFormatterSvc.pointsTransferDetailsFormat({
          loyaltyProgram: this.loyaltyProgram,
          processingDelay:
            this.loyaltyProgram.pointsTransferCapability.processingDelay,
          pointsTransferData: {
            firstName,
            lastName,
            number: accountNumber,
            dateOfBirth,
          },
          tcv2Settings: this.transferConnectV2SettingService,
        });
    }
  }

  // Show PA status if not failed, else show "your points have been refunded" text
  get orderFulfillmentStatus(): OrderItemStatus {
    const failedStatus = [OrderItemStatus.Failed, OrderItemStatus.Cancelled];

    if (failedStatus.includes(this.pointsTransferOrder.fulfillmentStatus)) {
      return OrderItemStatus.Failed;
    } else {
      return OrderItemStatus.Fulfilled;
    }
  }

  loyaltyProgramImageUrl(): URL | null {
    return this.loyaltyProgram.pointsTransferCapability.logoUrl;
  }

  // Translation keys
  redeemAmountString(amount: number): string {
    const params = this.redeemAmountParams(amount);

    return this.translateService.instant(
      'transfer_v2.success.redeem_amount',
      params,
    );
  }

  redeemAmountParams(amount: number): { [key: string]: string } {
    const targetCurrencyNameAttribute =
      this.targetLoyaltyCurrencyNameAttribute[
        this.loyaltyProgram.transferConnectIdentifier
      ] ?? this.loyaltyProgramNameAttribute;

    return {
      sourceAmount: this.numberService.formatNumber(amount),
      sourceLoyaltyCurrency:
        // @ts-ignore
        this.loyaltyCurrencyService.loyaltyCurrencySelected[
          this.loyaltyProgramNameAttribute
        ],
      targetAmount: this.numberService.formatNumber(
        this.pointsTransferOrderItemData.transferAmount,
      ),
      targetLoyaltyCurrency:
        targetCurrencyNameAttribute === 'name'
          ? this.pointsTransferOrderItemData.loyaltyCurrencyName
          : this.pointsTransferOrderItemData.loyaltyCurrencyShortName,
    };
  }

  refundedMessageParams(): { loyaltyCurrency: string } {
    return {
      loyaltyCurrency:
        // @ts-ignore
        this.loyaltyCurrencyService.loyaltyCurrencySelected.shortName,
    };
  }
}
